import { orderBy, toLower } from "lodash";
import ProductVariant from "../models/ProductVariant";
import { isListNotEmpty, isNumberGreaterThanOrEqualToZero, isObjectNotEmpty, isStringNotEmpty } from "./objectUtils";

export const VARIANT_MATCH_STRATEGIES = {
    NONE: 'NONE',
    SAME_DISPLAY_ORDER: 'SAME_DISPLAY_ORDER',
    SAME_STRENGTH: 'SAME_STRENGTH',
    SAME_TITRATION_LEVEL: 'SAME_TITRATION_LEVEL',
};

const LEGACY_COMMERCIAL_EQUIVALENCY_GROUP_RULES = [
    {
        id: 'COMPOUNDED_SEMAGLUTIDE',
        name: 'Compounded Semaglutide',
        description: 'Commercially equivalent compounded semaglutide formulations.',
        enabled: true,
        variantMatchStrategy: VARIANT_MATCH_STRATEGIES.SAME_STRENGTH,
        productSkus: ['SEMAGLUTIDE', 'SEMAGLUTIDE-G', 'SEMAGLUTIDE-GM']
    },
    {
        id: 'COMPOUNDED_SEMAGLUTIDE_MICRODOSING',
        name: 'Compounded Semaglutide Microdosing',
        description: 'Commercially equivalent compounded semaglutide microdosing formulations.',
        enabled: true,
        variantMatchStrategy: VARIANT_MATCH_STRATEGIES.SAME_STRENGTH,
        productSkus: ['SEMAGLUTIDE-MICRODOSING', 'SEMAGLUTIDE-G-MICRODOSING']
    },
    {
        id: 'COMPOUNDED_TIRZEPATIDE',
        name: 'Compounded Tirzepatide',
        description: 'Commercially equivalent compounded tirzepatide formulations.',
        enabled: true,
        variantMatchStrategy: VARIANT_MATCH_STRATEGIES.SAME_STRENGTH,
        productSkus: ['TIRZEPATIDE', 'TIRZEPATIDE-G', 'TIRZEPATIDE-GM']
    },
    {
        id: 'COMPOUNDED_TIRZEPATIDE_MICRODOSING',
        name: 'Compounded Tirzepatide Microdosing',
        description: 'Commercially equivalent compounded tirzepatide microdosing formulations.',
        enabled: true,
        variantMatchStrategy: VARIANT_MATCH_STRATEGIES.SAME_STRENGTH,
        productSkus: ['TIRZEPATIDE-MICRODOSING', 'TIRZEPATIDE-G-MICRODOSING']
    },
    {
        id: 'B12_INJECTIONS',
        name: 'B12 Injections',
        description: 'Commercially equivalent B12 injection formulations used as state/supplier availability substitutes.',
        enabled: true,
        variantMatchStrategy: VARIANT_MATCH_STRATEGIES.NONE,
        productSkus: ['MIC-B12', 'B12']
    }
];

const normalizeSku = (value) => {
    return isStringNotEmpty(value) ? toLower(value.trim()) : '';
};

export const getLegacyCommercialEquivalencyGroup = (value) => {
    const normalizedValue = normalizeSku(value);
    if (!isStringNotEmpty(normalizedValue)) {
        return null;
    }

    return LEGACY_COMMERCIAL_EQUIVALENCY_GROUP_RULES.find((rule) => {
        return rule.productSkus.some((productSku) => normalizeSku(productSku) === normalizedValue);
    }) ?? null;
};

export const getCommercialEquivalencyGroupById = (commercialEquivalencyGroupId, commercialEquivalencyGroups = []) => {
    return isListNotEmpty(commercialEquivalencyGroups) && isStringNotEmpty(commercialEquivalencyGroupId)
        ? commercialEquivalencyGroups.find((commercialEquivalencyGroup) => commercialEquivalencyGroup.id === commercialEquivalencyGroupId) ?? null
        : null;
};

export const getEffectiveCommercialEquivalencyGroup = (product, commercialEquivalencyGroups = []) => {
    if (isObjectNotEmpty(product) && isStringNotEmpty(product.commercialEquivalencyGroupId)) {
        return getCommercialEquivalencyGroupById(product.commercialEquivalencyGroupId, commercialEquivalencyGroups)
            ?? {
                id: product.commercialEquivalencyGroupId,
                enabled: true,
                variantMatchStrategy: VARIANT_MATCH_STRATEGIES.NONE,
            };
    }

    return getLegacyCommercialEquivalencyGroup(product?.sku ?? product?.shortSku ?? null);
};

export const getEffectiveCommercialEquivalencyGroupId = (product) => {
    if (isObjectNotEmpty(product) && isStringNotEmpty(product.commercialEquivalencyGroupId)) {
        return product.commercialEquivalencyGroupId;
    }

    return getLegacyCommercialEquivalencyGroup(product?.sku ?? product?.shortSku ?? null)?.id ?? null;
};

export const getProductsByCommercialEquivalencyGroupId = (commercialEquivalencyGroupId, products = []) => {
    if (!isStringNotEmpty(commercialEquivalencyGroupId) || !isListNotEmpty(products)) {
        return [];
    }

    return products.filter((product) => getEffectiveCommercialEquivalencyGroupId(product) === commercialEquivalencyGroupId);
};

export const getCommerciallyEquivalentProducts = ({ product = null, products = [] }) => {
    const commercialEquivalencyGroupId = getEffectiveCommercialEquivalencyGroupId(product);
    if (!isStringNotEmpty(commercialEquivalencyGroupId)) {
        return isObjectNotEmpty(product) ? [product] : [];
    }

    const groupProducts = getProductsByCommercialEquivalencyGroupId(commercialEquivalencyGroupId, products);
    if (isListNotEmpty(groupProducts)) {
        return orderBy(groupProducts, ['displayOrder', 'name'], ['asc', 'asc']);
    }

    return isObjectNotEmpty(product) ? [product] : [];
};

const getEnabledVariantsForProduct = (productId, variants = []) => {
    if (!isStringNotEmpty(productId) || !isListNotEmpty(variants)) {
        return [];
    }

    return orderBy(
        variants
            .filter((variant) => variant.productId === productId && variant.enabled === true)
            .map((variant) => new ProductVariant(variant)),
        ['displayOrder', 'titrationLevel', 'name'],
        ['asc', 'asc', 'asc']
    );
};

const findSourceVariant = ({ sourceProduct = null, sourceVariantId = null, sourceVariantLevel = null, variants = [] }) => {
    const sourceVariants = getEnabledVariantsForProduct(sourceProduct?.id ?? null, variants);

    if (isStringNotEmpty(sourceVariantId)) {
        const explicitVariant = sourceVariants.find((variant) => variant.id === sourceVariantId);
        if (isObjectNotEmpty(explicitVariant)) {
            return explicitVariant;
        }
    }

    if (isNumberGreaterThanOrEqualToZero(sourceVariantLevel)) {
        const levelVariant = sourceVariants.find((variant) => variant.titrationLevel === sourceVariantLevel);
        if (isObjectNotEmpty(levelVariant)) {
            return levelVariant;
        }
    }

    return sourceVariants[0] ?? null;
};

export const resolveCommercialEquivalentVariant = ({
    sourceProduct = null,
    targetProduct = null,
    sourceVariantId = null,
    sourceVariantLevel = null,
    requestedVariantId = null,
    variants = [],
    commercialEquivalencyGroup = null
}) => {
    const targetVariants = getEnabledVariantsForProduct(targetProduct?.id ?? null, variants);
    if (!isListNotEmpty(targetVariants)) {
        return null;
    }

    if (isStringNotEmpty(requestedVariantId)) {
        return targetVariants.find((variant) => variant.id === requestedVariantId) ?? null;
    }

    const sourceVariant = findSourceVariant({
        sourceProduct,
        sourceVariantId,
        sourceVariantLevel,
        variants
    });

    if (sourceProduct?.id === targetProduct?.id) {
        if (isObjectNotEmpty(sourceVariant)) {
            const currentVariant = targetVariants.find((variant) => variant.id === sourceVariant.id);
            if (isObjectNotEmpty(currentVariant)) {
                return currentVariant;
            }
        }

        return targetVariants[0] ?? null;
    }

    if (!isObjectNotEmpty(sourceVariant)) {
        return targetVariants.length === 1 ? targetVariants[0] : null;
    }

    const variantMatchStrategy = commercialEquivalencyGroup?.variantMatchStrategy ?? VARIANT_MATCH_STRATEGIES.NONE;

    if (variantMatchStrategy === VARIANT_MATCH_STRATEGIES.SAME_DISPLAY_ORDER) {
        return targetVariants.find((variant) => variant.displayOrder === sourceVariant.displayOrder) ?? null;
    }

    if (variantMatchStrategy === VARIANT_MATCH_STRATEGIES.SAME_STRENGTH) {
        return targetVariants.find((variant) => variant.strength === sourceVariant.strength) ?? null;
    }

    if (variantMatchStrategy === VARIANT_MATCH_STRATEGIES.SAME_TITRATION_LEVEL) {
        return targetVariants.find((variant) => variant.titrationLevel === sourceVariant.titrationLevel) ?? null;
    }

    return targetVariants.length === 1 ? targetVariants[0] : null;
};

export const canUseCommercialEquivalentSwap = ({ sourceProduct = null, targetProduct = null }) => {
    if (!isObjectNotEmpty(sourceProduct) || !isObjectNotEmpty(targetProduct)) {
        return false;
    }

    if (sourceProduct.id === targetProduct.id) {
        return true;
    }

    const sourceGroupId = getEffectiveCommercialEquivalencyGroupId(sourceProduct);
    const targetGroupId = getEffectiveCommercialEquivalencyGroupId(targetProduct);

    return isStringNotEmpty(sourceGroupId) && sourceGroupId === targetGroupId;
};
